import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { editQuotePath, newQuotePath } from "../../router/paths";
import { showMultipleToasts } from "../../utils/apiSetup";
import { featureFlags } from "../../components/FeatureFlag/featureFlagConst";
import { getFeatureFlagsConfiguration, getGlobalConfiguration } from "../../utils/getGlobalConfiguration";
import { isCustomEvent } from "../guards";
import { useSelector } from "react-redux";
import { State } from "../../configureStore";
import { featureFlagsChangeAtom } from "../../atoms/atoms";
import { useAtom } from "jotai";

interface QuotesProps {
  id: string;
}

const Quotes = memo(function Policies({ id }: QuotesProps) {
  const [eventListenerRef, setEventListenerRef] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const history = useHistory();
  const isLoading = useSelector((state: State) => state.api.configurations.agentConfigurations.isFetching);
  const [featureFlagValues] = useAtom(featureFlagsChangeAtom);

  const featureFlag = JSON.stringify({
    quote: {
      newQuoteButton: getFeatureFlagsConfiguration(featureFlags.customer.customerView.newQuoteButton),
      cancel: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.cancel),
      cancelQuoteOnPolicy: getFeatureFlagsConfiguration(
        featureFlags.customer.customerView.quoteActions.cancelQuoteOnPolicy,
      ),
      cancelQuoteOnRenewal: getFeatureFlagsConfiguration(
        featureFlags.customer.customerView.quoteActions.cancelQuoteOnRenewal,
      ),
      edit: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.edit),
      sendTo: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.sendTo),
      setInForce: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.setInForce),
      sendToAgent: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.sendToAgent),
      sendToAgentFirm: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.sendToAgentFirm),
      sendToCustomer: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.sendToCustomer),
      acceptQuoteOnPolicy: getFeatureFlagsConfiguration(
        featureFlags.customer.customerView.quoteActions.acceptQuoteOnPolicy,
      ),
      acceptQuoteOnRenewal: getFeatureFlagsConfiguration(
        featureFlags.customer.customerView.quoteActions.acceptQuoteOnRenewal,
      ),
      addNoteToQuote: getFeatureFlagsConfiguration(featureFlags.customer.customerView.quoteActions.addNoteToQuote),
      addGeneralNoteToQuote: getFeatureFlagsConfiguration(
        featureFlags.customer.customerView.quoteActions.addGeneralNoteToQuote,
      ),
    },
  });

  const translations = JSON.stringify({
    quote: t("portal:widgets.quoteList", {
      returnObjects: true,
      interpolation: { skipOnVariables: true },
    }),
    policyStatus: t("portal:policyStatus", {
      returnObjects: true,
      interpolation: { skipOnVariables: true },
    }),
  });

  const pageSize = getGlobalConfiguration("portfolioWidgets.quotesPageSize");

  useEffect(() => {
    const handleReturnErrors = (event: Event) => {
      if (!isCustomEvent<{ errors?: string[] }>(event)) return;

      const { errors } = event.detail;

      if (errors) {
        showMultipleToasts(errors);
      }
    };

    const handleReturnMessages = (event: Event) => {
      if (!isCustomEvent<{ messages?: string[] }>(event)) return;

      const { messages } = event.detail;

      if (messages) {
        showMultipleToasts(messages);
      }
    };

    const handleNewQuoteClick = () => {
      history.push(newQuotePath(id));
    };

    const editQuote = (event: Event) => {
      const { uuid } = (event as CustomEvent<{ uuid: string }>).detail;

      history.push(editQuotePath({ customerId: id, quoteId: uuid }));
    };

    const handleOnSuccessAction = (event: Event) => {
      if (!isCustomEvent<{ messages?: string[]; action?: string }>(event)) return;

      const { messages, action } = event.detail;
      if (action === "fetch-quotes") {
        return;
      }

      if (messages) {
        showMultipleToasts(messages);
      }
    };

    if (eventListenerRef) {
      eventListenerRef.addEventListener("new-quote", handleNewQuoteClick);
      eventListenerRef.addEventListener("quote-edit", editQuote);
      eventListenerRef.addEventListener("success-widget-action", handleOnSuccessAction);
      eventListenerRef.addEventListener("widget-message", handleReturnMessages);
      eventListenerRef.addEventListener("widget-error", handleReturnErrors);
    }

    return () => {
      if (eventListenerRef) {
        eventListenerRef.removeEventListener("new-quote", handleNewQuoteClick);
        eventListenerRef.removeEventListener("quote-edit", editQuote);
        eventListenerRef.removeEventListener("success-widget-action", handleOnSuccessAction);
        eventListenerRef.removeEventListener("widget-message", handleReturnMessages);
        eventListenerRef.removeEventListener("widget-error", handleReturnErrors);
      }
    };
  }, [eventListenerRef, history, id]);

  //  Widget must not render until feature flags are resolved.
  //  featureFlagValues is null on first render (before API responds),
  //  and isLoading covers the gap while agentConfigurations API is in flight.
  //  Without this guard, the widget renders with fallback flag values and flickers.
  if (isLoading || !featureFlagValues) {
    return null;
  }

  return (
    <ds-portfolio-quote-widget
      pagesize={pageSize}
      ref={setEventListenerRef}
      variant="quote"
      partyid={id}
      featureflags={featureFlag}
      translations={translations}
    />
  );
});

export default Quotes;
